/**
 * Move the on-screen counts to match a save, WITHOUT refetching.
 *
 * Refetching is the obvious way to keep numbers honest, but it pulls the page out from
 * under the labeller: a crop leaves the «Неразмеченные» queue the instant it is judged,
 * and a misclick has to stay fixable until they choose to reload. So the grid is left
 * exactly as it is and only the counters move.
 *
 * Callers apply each row AT MOST ONCE per fetch (see `accounted` in the annotator), which
 * is what makes this safe: `cur.items` still holds the server's original state, so
 * re-editing a row cannot decrement the same counter twice.
 *
 * Shelf-specific: a verdict can also be RETRACTED (`percentage: null`), which moves a row
 * back into the unlabelled queue. That direction does not exist for tables — a stage
 * there can only ever gain a verdict — so it is handled explicitly rather than inherited.
 */
import type { ShelfPoolSamples, View } from '@/api/shelfPool';

const UNLABELLED_VIEWS: View[] = ['unlabelled', 'cancelled', 'collected'];

export function applyShelfDelta(
  cur: ShelfPoolSamples,
  view: View,
  /** id → the value just written; `null` means the verdict was retracted. */
  written: Map<number, number | null>,
): ShelfPoolSamples {
  const labelled = { ...cur.facets.labelled };
  const origins = { ...cur.facets.unlabelled_origins };
  const perShelf = new Map(cur.facets.shelves.map(r => [r.shelf_id, { ...r }]));
  let total = cur.total;

  for (const it of cur.items) {
    if (!written.has(it.id)) continue;
    const now = written.get(it.id) ?? null;
    // `!= null` and not a falsy test: 0 is a real label (an empty shelf), and treating
    // it as "unlabelled" would silently under-count exactly the crops that matter most.
    const wasLabelled = it.percentage != null;
    const isLabelled = now != null;
    if (wasLabelled === isLabelled && it.source !== 'ai') continue;

    let left = false;                       // did the row leave the CURRENT view?
    if (!wasLabelled && isLabelled) {
      labelled.unlabelled -= 1;
      origins[it.origin] = (origins[it.origin] ?? 0) - 1;
      labelled.all += 1;
      labelled.human += 1;
      left = UNLABELLED_VIEWS.includes(view);
    } else if (wasLabelled && !isLabelled) {
      // Retracted: back to the queue it came from.
      labelled.unlabelled += 1;
      origins[it.origin] = (origins[it.origin] ?? 0) + 1;
      labelled.all -= 1;
      if (it.source === 'ai') labelled.ai -= 1; else labelled.human -= 1;
      left = !UNLABELLED_VIEWS.includes(view);
    } else if (it.source === 'ai' && isLabelled) {
      // A human took over a machine's guess: same queue, different owner.
      labelled.ai -= 1;
      labelled.human += 1;
      left = view === 'ai';
    }
    if (!left) continue;                    // still matches the filter — counts only
    total -= 1;
    const row = perShelf.get(it.shelf_id);
    if (row) row.count -= 1;
  }
  return {
    ...cur,
    total,
    facets: {
      ...cur.facets, labelled, unlabelled_origins: origins,
      shelves: [...perShelf.values()],
    },
  };
}
